import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teamMembersApi } from '@/services/api';
import { getCurrentUser } from '@/lib/supabase';
import type { Database } from '@/types/database.types';

type TeamMemberInsert = Database['public']['Tables']['team_members']['Insert'];
type TeamMemberUpdate = Database['public']['Tables']['team_members']['Update'];

/**
 * Hook to fetch all team members for the current user
 */
export const useTeamMembers = () => {
    return useQuery({
        queryKey: ['team-members'],
        queryFn: async () => {
            const { data, error } = await teamMembersApi.getAll();
            if (error) throw error;
            return data ?? [];
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Hook to fetch a single team member by ID
 */
export const useTeamMember = (id: number) => {
    return useQuery({
        queryKey: ['team-member', id],
        queryFn: () => teamMembersApi.getById(id),
        enabled: !!id,
        staleTime: 5 * 60 * 1000,
    });
};

/**
 * Hook to create a new team member
 */
export const useCreateTeamMember = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: Omit<TeamMemberInsert, 'user_id'>) => {
            // Get current authenticated user ID
            const user = await getCurrentUser();
            if (!user?.id) {
                throw new Error('User must be authenticated to create a team member');
            }
            return teamMembersApi.create({ ...data, user_id: user.id });
        },
        onSuccess: () => {
            // Invalidate and refetch team members list
            queryClient.invalidateQueries({ queryKey: ['team-members'] });
        },
    });
};

/**
 * Hook to update an existing team member
 */
export const useUpdateTeamMember = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, updates }: { id: number; updates: TeamMemberUpdate }) => {
            return teamMembersApi.update(id, updates);
        },
        onSuccess: (response) => {
            // Invalidate team members list and specific team member
            queryClient.invalidateQueries({ queryKey: ['team-members'] });
            if (response.data) {
                queryClient.invalidateQueries({ queryKey: ['team-member', response.data.id] });
            }
        },
    });
};

/**
 * Hook to delete a team member
 */
export const useDeleteTeamMember = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => teamMembersApi.delete(id),
        onSuccess: () => {
            // Invalidate team members list
            queryClient.invalidateQueries({ queryKey: ['team-members'] });
        },
    });
};

/**
 * Hook to check how many tasks are assigned to a team member
 */
export const useTeamMemberTaskCount = (memberId: number) => {
    return useQuery({
        queryKey: ['team-member-task-count', memberId],
        queryFn: () => teamMembersApi.getAssignedTasksCount(memberId),
        enabled: !!memberId,
    });
};

